import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import BlobButton from '../../components/BlobButton';

const blogPosts = [
  {
    id: 'art-of-storytelling',
    title: 'The Art of Storytelling: Tips from Bestselling Authors',
    image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    excerpt: 'Discover proven techniques to captivate readers and create compelling narratives that keep them turning pages.',
    author: 'Sarah Mitchell',
    date: 'Jan 15, 2025',
    readTime: '5 min read',
    category: 'Writing Tips'
  },
  {
    id: 'self-publishing-vs-traditional',
    title: 'Self-Publishing vs Traditional Publishing: Making the Right Choice',
    image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    excerpt: 'Understand the pros and cons of each publishing path to make an informed decision for your book.',
    author: 'Michael Chen',
    date: 'Jan 12, 2025',
    readTime: '7 min read',
    category: 'Publishing'
  },
  {
    id: 'book-marketing-strategies-2025',
    title: 'Book Marketing Strategies That Actually Work in 2025',
    image: 'https://images.unsplash.com/photo-1432821596592-e2c18b78144f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    excerpt: 'Learn cutting-edge marketing techniques to boost your book sales and reach wider audiences.',
    author: 'Emily Rodriguez',
    date: 'Jan 10, 2025',
    readTime: '6 min read',
    category: 'Marketing'
  },
  {
    id: 'cover-design-trends-2025',
    title: 'Cover Design Trends: What Readers Love in 2025',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    excerpt: 'Explore the latest trends in book cover design and how to create visually stunning covers.',
    author: 'James Anderson',
    date: 'Jan 8, 2025',
    readTime: '4 min read',
    category: 'Design'
  },
  {
    id: 'building-author-platform',
    title: 'Building Your Author Platform: A Complete Guide',
    image: 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    excerpt: 'Step-by-step guide to establishing a strong online presence and connecting with readers.',
    author: 'Rebecca Turner',
    date: 'Jan 5, 2025',
    readTime: '8 min read',
    category: 'Author Growth'
  },
  {
    id: 'future-of-publishing-ai',
    title: 'The Future of Publishing: AI and Technology',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    excerpt: 'How artificial intelligence and new technologies are reshaping the publishing industry.',
    author: 'David Park',
    date: 'Jan 3, 2025',
    readTime: '6 min read',
    category: 'Technology'
  }
];

const categories = ['All', 'Writing Tips', 'Publishing', 'Marketing', 'Design', 'Author Growth', 'Technology'];

const BlogList = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredPosts = selectedCategory === 'All'
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <>
      <Helmet>
        <title>Publishing Blog - Expert Tips & Insights | Rushmore Publishing</title>
        <meta name="description" content="Expert articles on book publishing, writing tips, marketing strategies, and industry insights from Rushmore Publishing." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white pt-24 pb-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Publishing <span className="text-amber-700">Blog</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Expert insights, tips, and guides to help you succeed in your publishing journey
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-amber-700 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Blog Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map(post => (
              <article key={post.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <Link to={`/blog/${post.id}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-amber-700 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-3 hover:text-amber-700 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {post.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.readTime}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4">
                      <span className="text-amber-700 font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                        Read More <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogList;